import * as THREE from 'three';
import ColorMap, { ColorMapOptions, Color, ColorArray } from './ColorMap';
import ColorMap2D, { ColorMap2DPreset, ColorMap2DOptions } from './ColorMap2D';
import { debugLog } from './debug';
import { VolumeTexture3D } from './textures/VolumeTexture3D';
import { createColormapTexture } from './textures/createColormapTexture';

export type BlendMode = 'normal' | 'additive' | 'multiply';

export interface LayerConfig {
  visible?: boolean;
  opacity?: number;
  blendMode?: BlendMode;
  order?: number;
}

export interface DataLayerConfig extends LayerConfig {
  range?: [number, number];
  threshold?: [number, number];
}

export interface VolumeProjectionLayerConfig extends DataLayerConfig {
  /** Voxel-to-world affine matrix (column-major) or Matrix4; used to derive world->voxel. */
  affineMatrix?: THREE.Matrix4 | ArrayLike<number>;
  /** Direct world->voxel transform (column-major) or Matrix4; overrides affineMatrix if provided. */
  worldToIJK?: THREE.Matrix4 | ArrayLike<number>;
  /** Simple voxel-to-world fallback when no affine is provided. */
  voxelSize?: [number, number, number];
  /** Simple voxel-to-world fallback when no affine is provided. */
  volumeOrigin?: [number, number, number];
  /** Colormap preset name (GPU path uses a 256x1 colormap texture). */
  colormap?: string;
  /** Use HalfFloatType on the GPU (saves memory, costs CPU conversion). */
  useHalfFloat?: boolean;
  /** Treat values equal to fillValue as transparent (alpha=0). */
  fillValue?: number;
}

export interface TwoDataLayerConfig extends LayerConfig {
  rangeX?: [number, number];
  rangeY?: [number, number];
  thresholdX?: [number, number];
  thresholdY?: [number, number];
}

export interface LayerUpdateData {
  opacity?: number;
  visible?: boolean;
  blendMode?: BlendMode;
  [key: string]: any;
}

export interface RGBALayerUpdateData extends LayerUpdateData {
  rgbaData?: Float32Array | number[];
}

export interface DataLayerUpdateData extends LayerUpdateData {
  data?: Float32Array | number[];
  indices?: Uint32Array | number[];
  colorMap?: ColorMap | string | Color[];
  range?: [number, number];
  threshold?: [number, number];
}

export interface VolumeProjectionLayerUpdateData extends LayerUpdateData {
  volumeData?: Float32Array | number[];
  colormap?: string;
  range?: [number, number];
  threshold?: [number, number];
  worldToIJK?: THREE.Matrix4 | ArrayLike<number>;
  affineMatrix?: THREE.Matrix4 | ArrayLike<number>;
  voxelSize?: [number, number, number];
  volumeOrigin?: [number, number, number];
  useHalfFloat?: boolean;
  fillValue?: number;
}

export interface TwoDataLayerUpdateData extends LayerUpdateData {
  dataX?: Float32Array | number[];
  dataY?: Float32Array | number[];
  indices?: Uint32Array | number[];
  colorMap?: ColorMap2D | ColorMap2DPreset;
  rangeX?: [number, number];
  rangeY?: [number, number];
  thresholdX?: [number, number];
  thresholdY?: [number, number];
}

export interface BaseLayerUpdateData extends LayerUpdateData {
  color?: number;
}

export interface LabelLayerOptions extends LayerConfig {
  labels: Uint32Array | Int32Array | number[];
  labelDefs: Array<{ id: number; color: THREE.ColorRepresentation; name?: string }>;
  defaultColor?: THREE.ColorRepresentation;
}

/**
 * Base class for all layer types
 */
export abstract class Layer {
  id: string;
  visible: boolean;
  opacity: number;
  blendMode: BlendMode;
  order: number;
  needsUpdate: boolean;
  /** Callback set by the parent surface to trigger re-compositing on change. */
  _onChangeCallback: (() => void) | null;
  private static _outlineCtor: any;
  private static _temporalCtor: any;

  constructor(id: string, config: LayerConfig = {}) {
    this.id = id;
    this.visible = config.visible !== undefined ? config.visible : true;
    this.opacity = config.opacity !== undefined ? config.opacity : 1.0;
    this.blendMode = config.blendMode || 'normal';
    this.order = config.order || 0;
    this.needsUpdate = true;
    this._onChangeCallback = null;
  }

  /** Notify the parent surface that this layer's data has changed. */
  protected _notifyChange(): void {
    this.needsUpdate = true;
    if (this._onChangeCallback) {
      this._onChangeCallback();
    }
  }

  setVisible(visible: boolean): void {
    if (this.visible !== visible) {
      this.visible = visible;
      this._notifyChange();
    }
  }

  setOpacity(opacity: number): void {
    opacity = Math.max(0, Math.min(1, opacity));
    if (this.opacity !== opacity) {
      this.opacity = opacity;
      this._notifyChange();
    }
  }

  setBlendMode(mode: BlendMode): void {
    const validModes: BlendMode[] = ['normal', 'additive', 'multiply'];
    if (validModes.includes(mode) && this.blendMode !== mode) {
      this.blendMode = mode;
      this._notifyChange();
    }
  }

  /**
   * Get RGBA values for this layer
   * Must be implemented by subclasses
   */
  abstract getRGBAData(vertexCount: number): Float32Array;

  /**
   * Update layer data
   * Must be implemented by subclasses
   */
  abstract update(data: LayerUpdateData): void;

  dispose(): void {
    // Override in subclasses if needed
  }

  /** Serialize common layer state for state persistence. Override in subclasses. */
  toStateJSON(): Record<string, unknown> {
    return {
      id: this.id,
      type: this.constructor.name,
      visible: this.visible,
      opacity: this.opacity,
      blendMode: this.blendMode,
      order: this.order
    };
  }

  static registerOutlineLayer(ctor: any): void {
    Layer._outlineCtor = ctor;
  }

  static registerTemporalLayer(ctor: any): void {
    Layer._temporalCtor = ctor;
  }

  private static get outlineCtor(): any {
    return Layer._outlineCtor;
  }

  /**
   * Create a concrete Layer instance from a plain object configuration.
   * Supports: base, rgba, data, outline.
   */
  static fromConfig(config: Record<string, any>): Layer {
    const { type, id } = config;
    if (!type || !id) {
      throw new Error('Layer.fromConfig requires type and id');
    }

    const commonConfig: LayerConfig = {
      visible: config.visible,
      opacity: config.opacity ?? (config.alpha !== undefined ? config.alpha : undefined),
      blendMode: config.blendMode,
      order: config.order
    };

    switch (type) {
      case 'base':
        return new BaseLayer(config.color ?? 0xcccccc, commonConfig);
      case 'rgba':
        if (!config.data) throw new Error('RGBALayer requires data');
        return new RGBALayer(id, config.data, commonConfig);
      case 'data':
        if (!config.data) throw new Error('DataLayer requires data');
        return new DataLayer(
          id,
          config.data,
          config.indices ?? null,
          config.cmap ?? config.colorMap ?? 'jet',
          {
            ...commonConfig,
            range: config.range,
            threshold: config.threshold
          }
        );
      case 'outline':
        if (!config.roiLabels) throw new Error('OutlineLayer requires roiLabels');
        if (!Layer.outlineCtor) throw new Error('OutlineLayer constructor not registered');
        return new Layer.outlineCtor(id, {
          roiLabels: config.roiLabels,
          color: config.color,
          opacity: commonConfig.opacity,
          width: config.width,
          halo: config.halo,
          haloColor: config.haloColor,
          haloWidth: config.haloWidth,
          offset: config.offset,
          roiSubset: config.roiSubset,
          visible: commonConfig.visible,
          blendMode: commonConfig.blendMode,
          order: commonConfig.order
        });
      case 'label':
        if (!config.labels || !config.labelDefs) {
          throw new Error('LabelLayer requires labels and labelDefs');
        }
        return new LabelLayer(id, {
          labels: config.labels,
          labelDefs: config.labelDefs,
          defaultColor: config.defaultColor,
          visible: commonConfig.visible,
          opacity: commonConfig.opacity,
          blendMode: commonConfig.blendMode,
          order: commonConfig.order
        });
      case 'twodata':
        if (!config.dataX || !config.dataY) {
          throw new Error('TwoDataLayer requires dataX and dataY');
        }
        return new TwoDataLayer(
          id,
          config.dataX,
          config.dataY,
          config.indices ?? null,
          config.cmap ?? config.colorMap ?? 'confidence',
          {
            ...commonConfig,
            rangeX: config.rangeX,
            rangeY: config.rangeY,
            thresholdX: config.thresholdX,
            thresholdY: config.thresholdY
          }
        );
      case 'temporal':
        if (!config.frames || !config.times) {
          throw new Error('TemporalDataLayer requires frames and times');
        }
        if (!Layer._temporalCtor) {
          throw new Error('TemporalDataLayer constructor not registered');
        }
        return new Layer._temporalCtor(
          id,
          config.frames,
          config.times,
          config.cmap ?? config.colorMap ?? 'jet',
          {
            ...commonConfig,
            range: config.range,
            threshold: config.threshold,
            factor: config.factor
          }
        );
      case 'volume':
        if (!config.volumeData || !config.dims) {
          throw new Error('VolumeProjectionLayer requires volumeData and dims');
        }
        return new VolumeProjectionLayer(
          id,
          config.volumeData,
          config.dims,
          {
            ...commonConfig,
            colormap: config.colormap ?? config.cmap ?? 'viridis',
            range: config.range,
            threshold: config.threshold,
            worldToIJK: config.worldToIJK,
            affineMatrix: config.affineMatrix ?? config.affine,
            voxelSize: config.voxelSize,
            volumeOrigin: config.volumeOrigin,
            useHalfFloat: config.useHalfFloat,
            fillValue: config.fillValue
          }
        );
      default:
        throw new Error(`Unsupported layer type: ${type}`);
    }
  }
}

/**
 * Layer with pre-computed RGBA values (dumb mode)
 */
export class RGBALayer extends Layer {
  private rgbaData: Float32Array | null = null;

  constructor(id: string, rgbaData: Float32Array | number[], config: LayerConfig = {}) {
    super(id, config);
    this.setRGBAData(rgbaData);
  }

  setRGBAData(rgbaData: Float32Array | number[]): void {
    if (!rgbaData) {
      throw new Error('RGBA data is required');
    }
    
    // Ensure it's a Float32Array
    this.rgbaData = rgbaData instanceof Float32Array 
      ? rgbaData 
      : new Float32Array(rgbaData);
    
    // Validate data length (should be divisible by 4)
    if (this.rgbaData.length % 4 !== 0) {
      throw new Error('RGBA data length must be divisible by 4');
    }
    
    this._notifyChange();
    debugLog(`RGBALayer ${this.id}: Set RGBA data with ${this.rgbaData.length / 4} vertices`);
  }

  getRGBAData(vertexCount: number): Float32Array {
    if (!this.rgbaData) {
      throw new Error('No RGBA data set');
    }
    
    const expectedLength = vertexCount * 4;
    if (this.rgbaData.length !== expectedLength) {
      console.warn(`RGBALayer ${this.id}: Data length mismatch. Expected ${expectedLength}, got ${this.rgbaData.length}`);
    }
    
    return this.rgbaData;
  }

  update(data: RGBALayerUpdateData): void {
    if (data.rgbaData) {
      this.setRGBAData(data.rgbaData);
    }
    if (data.opacity !== undefined) {
      this.setOpacity(data.opacity);
    }
    if (data.visible !== undefined) {
      this.setVisible(data.visible);
    }
    if (data.blendMode !== undefined) {
      this.setBlendMode(data.blendMode);
    }
  }

  toStateJSON(): Record<string, unknown> {
    return { ...super.toStateJSON(), type: 'rgba' };
  }
}

/**
 * Layer with data values and colormap (smart mode)
 */
export class DataLayer extends Layer {
  private data: Float32Array | null = null;
  private indices: Uint32Array | null = null;
  private colorMap: ColorMap | null = null;
  private colorMapName: string | null = null;
  private range: [number, number];
  private threshold: [number, number];
  private _cachedRGBABuffer: Float32Array | null = null;

  constructor(
    id: string, 
    data: Float32Array | number[], 
    indices: Uint32Array | number[] | null, 
    colorMap: ColorMap | string | Color[], 
    config: DataLayerConfig = {}
  ) {
    super(id, config);
    this.range = config.range || [0, 1];
    this.threshold = config.threshold || [0, 0];
    
    // Initialize data
    this.setData(data, indices);
    this.setColorMap(colorMap);
    
    // Apply initial settings
    if (this.colorMap) {
      this.colorMap.setRange(this.range);
      this.colorMap.setThreshold(this.threshold);
    }
  }

  setData(data: Float32Array | number[], indices?: Uint32Array | number[] | null): void {
    if (!data) {
      throw new Error('Data is required');
    }
    
    this.data = data instanceof Float32Array 
      ? data 
      : new Float32Array(data);
    
    if (indices) {
      this.indices = indices instanceof Uint32Array
        ? indices
        : new Uint32Array(indices);
    } else {
      // If no indices provided, assume 1:1 mapping
      this.indices = new Uint32Array(this.data.length);
      for (let i = 0; i < this.data.length; i++) {
        this.indices[i] = i;
      }
    }
    
    this._notifyChange();
    debugLog(`DataLayer ${this.id}: Set data with ${this.data.length} values`);
  }

  getData(): Float32Array | null {
    return this.data;
  }

  setColorMap(colorMap: ColorMap | string | Color[]): void {
    if (!colorMap) {
      throw new Error('ColorMap is required');
    }
    
    debugLog(`DataLayer ${this.id}: setColorMap called with`, colorMap);
    
    if (colorMap instanceof ColorMap) {
      this.colorMap = colorMap;
      this.colorMapName = 'custom';
      debugLog(`DataLayer ${this.id}: Set ColorMap instance directly`);
    } else if (typeof colorMap === 'string') {
      try {
        this.colorMap = ColorMap.fromPreset(colorMap);
        this.colorMapName = colorMap;
        debugLog(`DataLayer ${this.id}: Created ColorMap from preset: ${colorMap}`);
      } catch (err) {
        const presets = ColorMap.getAvailableMaps();
        const fallback = presets.includes('jet') ? 'jet' : (presets[0] || 'jet');
        console.warn(`DataLayer ${this.id}: preset "${colorMap}" unavailable, falling back to "${fallback}"`, err);
        this.colorMap = ColorMap.fromPreset(fallback);
        this.colorMapName = fallback;
      }
    } else if (Array.isArray(colorMap)) {
      this.colorMap = new ColorMap(colorMap);
      this.colorMapName = 'custom';
      debugLog(`DataLayer ${this.id}: Created ColorMap from color array`);
    } else {
      throw new Error('Invalid colorMap type');
    }
    
    // Apply current range and threshold
    this.colorMap.setRange(this.range);
    this.colorMap.setThreshold(this.threshold);
    
    // Invalidate cached RGBA buffer to force regeneration
    this._cachedRGBABuffer = null;
    this._notifyChange();
    debugLog(`DataLayer ${this.id}: ColorMap updated, needsUpdate = true`);
  }

  setRange(range: [number, number]): void {
    this.range = range;
    if (this.colorMap) {
      this.colorMap.setRange(range);
      this._notifyChange();
    }
  }

  setThreshold(threshold: [number, number]): void {
    this.threshold = threshold;
    if (this.colorMap) {
      this.colorMap.setThreshold(threshold);
      this._notifyChange();
    }
  }

  getRange(): [number, number] {
    return [...this.range] as [number, number];
  }

  getThreshold(): [number, number] {
    return [...this.threshold] as [number, number];
  }

  getColorMapName(): string {
    return this.colorMapName || 'custom';
  }

  toStateJSON(): Record<string, unknown> {
    return {
      ...super.toStateJSON(),
      type: 'data',
      colorMapName: this.getColorMapName(),
      range: this.getRange(),
      threshold: this.getThreshold()
    };
  }

  getRGBAData(vertexCount: number): Float32Array {
    if (!this.data || !this.colorMap || !this.indices) {
      throw new Error('Data, indices and colorMap must be set');
    }

    debugLog(`DataLayer ${this.id}: getRGBAData called for ${vertexCount} vertices`);
    debugLog(`DataLayer ${this.id}: data.length=${this.data.length}, indices.length=${this.indices.length}`);
    debugLog(`DataLayer ${this.id}: range=[${this.range[0].toFixed(4)}, ${this.range[1].toFixed(4)}]`);
    debugLog(`DataLayer ${this.id}: threshold=[${this.threshold[0].toFixed(4)}, ${this.threshold[1].toFixed(4)}]`);
    debugLog(`DataLayer ${this.id}: colormap=${this.colorMapName}, opacity=${this.opacity}`);

    // Reuse cached buffer if size matches to avoid GC pressure
    if (!this._cachedRGBABuffer || this._cachedRGBABuffer.length !== vertexCount * 4) {
      this._cachedRGBABuffer = new Float32Array(vertexCount * 4);
    }
    const rgbaData = this._cachedRGBABuffer;

    // Initialize with transparent black
    rgbaData.fill(0);

    // Track statistics for debugging
    let nonTransparentCount = 0;
    let transparentCount = 0;

    // Fill in colors for vertices with data
    for (let i = 0; i < this.indices.length && i < this.data.length; i++) {
      const vertexIndex: number = this.indices[i];
      const value = this.data[i];

      // Add bounds and NaN check for safety
      if (vertexIndex >= 0 && vertexIndex < vertexCount && isFinite(value)) {
        const color = this.colorMap.getColor(value);
        const offset = vertexIndex * 4;

        rgbaData[offset] = color[0];     // R
        rgbaData[offset + 1] = color[1]; // G
        rgbaData[offset + 2] = color[2]; // B
        rgbaData[offset + 3] = (color[3] ?? 1) * this.opacity; // A with layer opacity

        // Track transparency for debugging
        if (rgbaData[offset + 3] > 0) {
          nonTransparentCount++;
        } else {
          transparentCount++;
        }
      }
    }

    debugLog(`DataLayer ${this.id}: Generated colors - ${nonTransparentCount} visible, ${transparentCount} transparent`);

    // Sample a few values for debugging
    if (this.data.length > 0) {
      const sampleIdx = Math.floor(this.data.length / 2);
      const sampleValue = this.data[sampleIdx];
      const sampleColor = this.colorMap.getColor(sampleValue);
      debugLog(`DataLayer ${this.id}: Sample value[${sampleIdx}]=${sampleValue.toFixed(4)} -> RGBA=[${sampleColor.map(v => v.toFixed(3)).join(', ')}]`);
    }

    return rgbaData;
  }

  update(updates: DataLayerUpdateData): void {
    if (updates.data !== undefined) {
      this.setData(updates.data, updates.indices);
    }
    if (updates.colorMap !== undefined) {
      this.setColorMap(updates.colorMap);
    }
    if (updates.range !== undefined) {
      this.setRange(updates.range);
    }
    if (updates.threshold !== undefined) {
      this.setThreshold(updates.threshold);
    }
    if (updates.opacity !== undefined) {
      this.setOpacity(updates.opacity);
    }
    if (updates.visible !== undefined) {
      this.setVisible(updates.visible);
    }
    if (updates.blendMode !== undefined) {
      this.setBlendMode(updates.blendMode);
    }
  }

  dispose(): void {
    this.data = null;
    this.indices = null;
    this.colorMap = null;
    this._cachedRGBABuffer = null;
  }
}

/**
 * GPU volume-to-surface projection layer.
 *
 * In GPU compositing mode, this layer is evaluated in the vertex shader by sampling a
 * 3D texture (WebGL2 required). In CPU mode, this layer falls back to a per-vertex
 * lookup and colormap on the CPU.
 */
export class VolumeProjectionLayer extends Layer {
  private volumeData: Float32Array;
  private readonly dims: [number, number, number];
  private volumeTexture: VolumeTexture3D;
  private worldToIJK: THREE.Matrix4;
  private colorMapName: string;
  private colorMap: ColorMap;
  private colormapTexture: THREE.DataTexture;
  private range: [number, number];
  private threshold: [number, number];
  private fillValue: number;
  private attachedSurface: { geometry: { vertices: Float32Array }; mesh?: THREE.Mesh } | null = null;
  private rgbaBuffer: Float32Array | null = null;

  constructor(
    id: string,
    volumeData: Float32Array | number[],
    dims: [number, number, number],
    config: VolumeProjectionLayerConfig = {}
  ) {
    super(id, config);

    this.dims = dims;
    this.range = config.range || [0, 1];
    this.threshold = config.threshold || [0, 0];
    this.fillValue = config.fillValue ?? 0.0;

    this.volumeData = volumeData instanceof Float32Array ? volumeData : new Float32Array(volumeData);
    this.volumeTexture = new VolumeTexture3D(this.volumeData, dims[0], dims[1], dims[2], {
      useHalfFloat: config.useHalfFloat
    });

    this.worldToIJK = this.computeWorldToIJK(config);

    this.colorMapName = config.colormap ?? 'viridis';
    try {
      this.colorMap = ColorMap.fromPreset(this.colorMapName);
    } catch (err) {
      const presets = ColorMap.getAvailableMaps();
      const fallback = presets.includes('jet') ? 'jet' : (presets[0] || 'jet');
      console.warn(`VolumeProjectionLayer ${this.id}: preset "${this.colorMapName}" unavailable, falling back to "${fallback}"`, err);
      this.colorMapName = fallback;
      this.colorMap = ColorMap.fromPreset(fallback);
    }

    this.colorMap.setRange(this.range);
    this.colorMap.setThreshold(this.threshold);

    this.colormapTexture = createColormapTexture(this.colorMapName);
  }

  /**
   * Attach to a surface to enable CPU fallback sampling.
   * Called by MultiLayerNeuroSurface when the layer is added.
   */
  attach(surface: { geometry: { vertices: Float32Array }; mesh?: THREE.Mesh }): void {
    this.attachedSurface = surface;
    this._notifyChange();
  }

  detach(): void {
    this.attachedSurface = null;
  }

  getVolumeTexture(): VolumeTexture3D {
    return this.volumeTexture;
  }

  getColormapTexture(): THREE.DataTexture {
    return this.colormapTexture;
  }

  getWorldToIJK(): THREE.Matrix4 {
    return this.worldToIJK;
  }

  getVolumeDims(): THREE.Vector3 {
    return this.volumeTexture.dims;
  }

  getRange(): [number, number] {
    return [...this.range] as [number, number];
  }

  getThreshold(): [number, number] {
    return [...this.threshold] as [number, number];
  }

  getFillValue(): number {
    return this.fillValue;
  }

  setRange(range: [number, number]): void {
    this.range = range;
    this.colorMap.setRange(range);
    this._notifyChange();
  }

  setThreshold(threshold: [number, number]): void {
    this.threshold = threshold;
    this.colorMap.setThreshold(threshold);
    this._notifyChange();
  }

  setFillValue(fillValue: number): void {
    this.fillValue = fillValue;
    this._notifyChange();
  }

  setColormap(name: string): void {
    this.colorMapName = name;
    try {
      this.colorMap = ColorMap.fromPreset(name);
    } catch (err) {
      const presets = ColorMap.getAvailableMaps();
      const fallback = presets.includes('jet') ? 'jet' : (presets[0] || 'jet');
      console.warn(`VolumeProjectionLayer ${this.id}: preset "${name}" unavailable, falling back to "${fallback}"`, err);
      this.colorMapName = fallback;
      this.colorMap = ColorMap.fromPreset(fallback);
    }

    this.colorMap.setRange(this.range);
    this.colorMap.setThreshold(this.threshold);

    if (this.colormapTexture) {
      this.colormapTexture.dispose();
    }
    this.colormapTexture = createColormapTexture(this.colorMapName);
    this._notifyChange();
  }

  setWorldToIJK(matrix: THREE.Matrix4 | ArrayLike<number>): void {
    this.worldToIJK = matrix instanceof THREE.Matrix4
      ? matrix.clone()
      : new THREE.Matrix4().fromArray(Array.from(matrix));
    this._notifyChange();
  }

  updateVolumeData(data: Float32Array | number[]): void {
    this.volumeData = data instanceof Float32Array ? data : new Float32Array(data);
    this.volumeTexture.updateData(this.volumeData);
    this._notifyChange();
  }

  getRGBAData(vertexCount: number): Float32Array {
    if (!this.attachedSurface) {
      throw new Error('VolumeProjectionLayer.getRGBAData requires attachment to a surface');
    }

    const vertices = this.attachedSurface.geometry.vertices;
    if (vertices.length / 3 !== vertexCount) {
      console.warn(`VolumeProjectionLayer ${this.id}: vertexCount mismatch; expected ${vertices.length / 3}, got ${vertexCount}`);
    }

    if (!this.rgbaBuffer || this.rgbaBuffer.length !== vertexCount * 4) {
      this.rgbaBuffer = new Float32Array(vertexCount * 4);
    }

    const rgba = this.rgbaBuffer;
    rgba.fill(0);

    const mesh = this.attachedSurface.mesh;
    if (mesh && typeof mesh.updateMatrixWorld === 'function') {
      mesh.updateMatrixWorld(true);
    }

    const worldMatrix = mesh ? mesh.matrixWorld : new THREE.Matrix4();
    const we = worldMatrix.elements;
    const me = this.worldToIJK.elements;

    const nx = this.dims[0];
    const ny = this.dims[1];
    const nz = this.dims[2];
    const data = this.volumeData;

    for (let vi = 0; vi < vertexCount; vi++) {
      const base = vi * 3;
      const x = vertices[base];
      const y = vertices[base + 1];
      const z = vertices[base + 2];

      // Local -> world
      const wx = we[0] * x + we[4] * y + we[8] * z + we[12];
      const wy = we[1] * x + we[5] * y + we[9] * z + we[13];
      const wz = we[2] * x + we[6] * y + we[10] * z + we[14];

      // World -> IJK
      const ijkX = me[0] * wx + me[4] * wy + me[8] * wz + me[12];
      const ijkY = me[1] * wx + me[5] * wy + me[9] * wz + me[13];
      const ijkZ = me[2] * wx + me[6] * wy + me[10] * wz + me[14];

      // Bounds check in normalized texture coordinates (matches shader behavior)
      const uvwX = (ijkX + 0.5) / nx;
      const uvwY = (ijkY + 0.5) / ny;
      const uvwZ = (ijkZ + 0.5) / nz;

      if (
        uvwX < 0 || uvwX > 1 ||
        uvwY < 0 || uvwY > 1 ||
        uvwZ < 0 || uvwZ > 1
      ) {
        continue;
      }

      // Nearest-neighbor sample with clamp-to-edge behavior.
      const i = Math.min(nx - 1, Math.max(0, Math.floor(ijkX + 0.5)));
      const j = Math.min(ny - 1, Math.max(0, Math.floor(ijkY + 0.5)));
      const k = Math.min(nz - 1, Math.max(0, Math.floor(ijkZ + 0.5)));
      const idx = i + nx * j + nx * ny * k;
      const value = data[idx];

      if (!isFinite(value)) continue;
      if (Math.abs(value - this.fillValue) < 1e-6) continue;

      const color = this.colorMap.getColor(value);
      const offset = vi * 4;
      rgba[offset] = color[0];
      rgba[offset + 1] = color[1];
      rgba[offset + 2] = color[2];
      rgba[offset + 3] = (color.length === 4 ? (color[3] as number) : 1);
    }

    this.needsUpdate = false;
    return rgba;
  }

  update(updates: VolumeProjectionLayerUpdateData): void {
    if (updates.volumeData !== undefined) {
      this.updateVolumeData(updates.volumeData);
    }
    if (updates.colormap !== undefined) {
      this.setColormap(updates.colormap);
    }
    if (updates.range !== undefined) {
      this.setRange(updates.range);
    }
    if (updates.threshold !== undefined) {
      this.setThreshold(updates.threshold);
    }
    if (updates.worldToIJK !== undefined) {
      this.setWorldToIJK(updates.worldToIJK);
    } else if (updates.affineMatrix !== undefined || updates.voxelSize !== undefined || updates.volumeOrigin !== undefined) {
      this.worldToIJK = this.computeWorldToIJK({
        ...updates,
        range: this.range,
        threshold: this.threshold,
        colormap: this.colorMapName
      });
      this._notifyChange();
    }
    if (updates.fillValue !== undefined) {
      this.setFillValue(updates.fillValue);
    }
    if (updates.opacity !== undefined) {
      this.setOpacity(updates.opacity);
    }
    if (updates.visible !== undefined) {
      this.setVisible(updates.visible);
    }
    if (updates.blendMode !== undefined) {
      this.setBlendMode(updates.blendMode);
    }
  }

  toStateJSON(): Record<string, unknown> {
    return {
      ...super.toStateJSON(),
      type: 'volume',
      colorMapName: this.colorMapName,
      range: [...this.range],
      threshold: [...this.threshold],
      fillValue: this.fillValue
    };
  }

  dispose(): void {
    this.detach();
    if (this.colormapTexture) {
      this.colormapTexture.dispose();
    }
    if (this.volumeTexture) {
      this.volumeTexture.dispose();
    }
    this.rgbaBuffer = null;
  }

  private computeWorldToIJK(config: VolumeProjectionLayerConfig | VolumeProjectionLayerUpdateData): THREE.Matrix4 {
    if (config.worldToIJK) {
      return config.worldToIJK instanceof THREE.Matrix4
        ? config.worldToIJK.clone()
        : new THREE.Matrix4().fromArray(Array.from(config.worldToIJK));
    }

    let voxelToWorld: THREE.Matrix4;
    if (config.affineMatrix) {
      voxelToWorld = config.affineMatrix instanceof THREE.Matrix4
        ? config.affineMatrix.clone()
        : new THREE.Matrix4().fromArray(Array.from(config.affineMatrix));
    } else {
      const voxelSize = config.voxelSize ?? [1, 1, 1];
      const origin = config.volumeOrigin ?? [0, 0, 0];
      voxelToWorld = new THREE.Matrix4().set(
        voxelSize[0], 0, 0, origin[0],
        0, voxelSize[1], 0, origin[1],
        0, 0, voxelSize[2], origin[2],
        0, 0, 0, 1
      );
    }

    const worldToVoxel = voxelToWorld.clone();
    const det = worldToVoxel.determinant();
    if (Math.abs(det) < 1e-10) {
      throw new Error('VolumeProjectionLayer: voxel-to-world matrix is singular (determinant ≈ 0). Check voxelSize and affineMatrix.');
    }
    return worldToVoxel.invert();
  }
}

/**
 * Layer with two data values mapped to a 2D colormap.
 *
 * Maps two scalar fields (X and Y) to colors using a 2D colormap texture.
 * Useful for visualizing relationships between variables, such as:
 * - Effect size (X) vs. statistical confidence (Y)
 * - Activation magnitude (X) vs. significance (Y)
 * - Any two correlated or independent scalar fields
 *
 * @example
 * ```typescript
 * const layer = new TwoDataLayer(
 *   'effect-confidence',
 *   effectSizeData,    // X values
 *   confidenceData,    // Y values
 *   indices,
 *   'confidence',      // 2D colormap preset
 *   {
 *     rangeX: [-2, 2],
 *     rangeY: [0, 1],
 *     thresholdY: [0, 0.05]  // Hide low-confidence values
 *   }
 * );
 * ```
 */
export class TwoDataLayer extends Layer {
  private dataX: Float32Array | null = null;
  private dataY: Float32Array | null = null;
  private indices: Uint32Array | null = null;
  private colorMap: ColorMap2D | null = null;
  private colorMapName: ColorMap2DPreset | 'custom' = 'confidence';
  private rangeX: [number, number];
  private rangeY: [number, number];
  private thresholdX: [number, number];
  private thresholdY: [number, number];

  /** Flag to identify this as a 2D data layer for GPU compositor */
  readonly is2DLayer: boolean = true;

  constructor(
    id: string,
    dataX: Float32Array | number[],
    dataY: Float32Array | number[],
    indices: Uint32Array | number[] | null,
    colorMap: ColorMap2D | ColorMap2DPreset,
    config: TwoDataLayerConfig = {}
  ) {
    super(id, config);
    this.rangeX = config.rangeX || [0, 1];
    this.rangeY = config.rangeY || [0, 1];
    this.thresholdX = config.thresholdX || [0, 0];
    this.thresholdY = config.thresholdY || [0, 0];

    // Initialize data
    this.setData(dataX, dataY, indices);
    this.setColorMap(colorMap);
  }

  setData(
    dataX: Float32Array | number[],
    dataY: Float32Array | number[],
    indices?: Uint32Array | number[] | null
  ): void {
    if (!dataX || !dataY) {
      throw new Error('Both dataX and dataY are required');
    }

    this.dataX = dataX instanceof Float32Array ? dataX : new Float32Array(dataX);
    this.dataY = dataY instanceof Float32Array ? dataY : new Float32Array(dataY);

    if (this.dataX.length !== this.dataY.length) {
      throw new Error('dataX and dataY must have the same length');
    }

    if (indices) {
      this.indices = indices instanceof Uint32Array
        ? indices
        : new Uint32Array(indices);
    } else {
      // If no indices provided, assume 1:1 mapping
      this.indices = new Uint32Array(this.dataX.length);
      for (let i = 0; i < this.dataX.length; i++) {
        this.indices[i] = i;
      }
    }

    this._notifyChange();
    debugLog(`TwoDataLayer ${this.id}: Set data with ${this.dataX.length} values`);
  }

  getDataX(): Float32Array | null {
    return this.dataX;
  }

  getDataY(): Float32Array | null {
    return this.dataY;
  }

  setColorMap(colorMap: ColorMap2D | ColorMap2DPreset): void {
    if (!colorMap) {
      throw new Error('ColorMap is required');
    }

    if (colorMap instanceof ColorMap2D) {
      this.colorMap = colorMap;
      this.colorMapName = 'custom';
    } else {
      // It's a preset name
      this.colorMap = ColorMap2D.fromPreset(colorMap, 256, {
        rangeX: this.rangeX,
        rangeY: this.rangeY,
        thresholdX: this.thresholdX,
        thresholdY: this.thresholdY
      });
      this.colorMapName = colorMap;
    }

    // Apply current ranges and thresholds
    this.colorMap.setRangeX(this.rangeX);
    this.colorMap.setRangeY(this.rangeY);
    this.colorMap.setThresholdX(this.thresholdX);
    this.colorMap.setThresholdY(this.thresholdY);

    this._notifyChange();
    debugLog(`TwoDataLayer ${this.id}: ColorMap set to ${this.colorMapName}`);
  }

  getColorMap(): ColorMap2D | null {
    return this.colorMap;
  }

  setRangeX(range: [number, number]): void {
    this.rangeX = range;
    if (this.colorMap) {
      this.colorMap.setRangeX(range);
      this._notifyChange();
    }
  }

  setRangeY(range: [number, number]): void {
    this.rangeY = range;
    if (this.colorMap) {
      this.colorMap.setRangeY(range);
      this._notifyChange();
    }
  }

  setThresholdX(threshold: [number, number]): void {
    this.thresholdX = threshold;
    if (this.colorMap) {
      this.colorMap.setThresholdX(threshold);
      this._notifyChange();
    }
  }

  setThresholdY(threshold: [number, number]): void {
    this.thresholdY = threshold;
    if (this.colorMap) {
      this.colorMap.setThresholdY(threshold);
      this._notifyChange();
    }
  }

  getRangeX(): [number, number] { return [...this.rangeX] as [number, number]; }
  getRangeY(): [number, number] { return [...this.rangeY] as [number, number]; }
  getThresholdX(): [number, number] { return [...this.thresholdX] as [number, number]; }
  getThresholdY(): [number, number] { return [...this.thresholdY] as [number, number]; }
  getColorMapName(): string { return this.colorMapName; }

  getRGBAData(vertexCount: number): Float32Array {
    if (!this.dataX || !this.dataY || !this.colorMap || !this.indices) {
      throw new Error('Data, indices and colorMap must be set');
    }

    debugLog(`TwoDataLayer ${this.id}: getRGBAData called for ${vertexCount} vertices`);

    const rgbaData = new Float32Array(vertexCount * 4);
    rgbaData.fill(0); // Initialize with transparent black

    let nonTransparentCount = 0;

    // Fill in colors for vertices with data
    // Local references for type narrowing
    const indices = this.indices;
    const dataX = this.dataX;
    const dataY = this.dataY;
    const colorMap = this.colorMap;

    for (let i = 0; i < indices.length && i < dataX.length; i++) {
      const vertexIndex = indices[i];
      const valueX = dataX[i];
      const valueY = dataY[i];

      if (vertexIndex >= 0 && vertexIndex < vertexCount) {
        const color = colorMap.getColor(valueX, valueY);
        const offset = vertexIndex * 4;

        rgbaData[offset] = color[0];
        rgbaData[offset + 1] = color[1];
        rgbaData[offset + 2] = color[2];
        rgbaData[offset + 3] = color[3] * this.opacity;

        if (rgbaData[offset + 3] > 0) {
          nonTransparentCount++;
        }
      }
    }

    debugLog(`TwoDataLayer ${this.id}: Generated colors - ${nonTransparentCount} visible`);
    return rgbaData;
  }

  update(updates: TwoDataLayerUpdateData): void {
    if (updates.dataX !== undefined || updates.dataY !== undefined) {
      const newDataX = updates.dataX !== undefined
        ? (updates.dataX instanceof Float32Array ? updates.dataX : new Float32Array(updates.dataX))
        : this.dataX;
      const newDataY = updates.dataY !== undefined
        ? (updates.dataY instanceof Float32Array ? updates.dataY : new Float32Array(updates.dataY))
        : this.dataY;

      if (newDataX && newDataY) {
        this.setData(newDataX, newDataY, updates.indices);
      }
    }
    if (updates.colorMap !== undefined) {
      this.setColorMap(updates.colorMap);
    }
    if (updates.rangeX !== undefined) {
      this.setRangeX(updates.rangeX);
    }
    if (updates.rangeY !== undefined) {
      this.setRangeY(updates.rangeY);
    }
    if (updates.thresholdX !== undefined) {
      this.setThresholdX(updates.thresholdX);
    }
    if (updates.thresholdY !== undefined) {
      this.setThresholdY(updates.thresholdY);
    }
    if (updates.opacity !== undefined) {
      this.setOpacity(updates.opacity);
    }
    if (updates.visible !== undefined) {
      this.setVisible(updates.visible);
    }
    if (updates.blendMode !== undefined) {
      this.setBlendMode(updates.blendMode);
    }
  }

  toStateJSON(): Record<string, unknown> {
    return {
      ...super.toStateJSON(),
      type: 'twodata',
      colorMapName: this.getColorMapName(),
      rangeX: this.getRangeX(),
      rangeY: this.getRangeY(),
      thresholdX: this.getThresholdX(),
      thresholdY: this.getThresholdY()
    };
  }

  dispose(): void {
    this.dataX = null;
    this.dataY = null;
    this.indices = null;
    if (this.colorMap) {
      this.colorMap.dispose();
      this.colorMap = null;
    }
  }
}

/**
 * Base layer representing the brain surface itself
 */
export class BaseLayer extends Layer {
  private color: number;

  constructor(color: number = 0xcccccc, config: LayerConfig = {}) {
    super('base', { ...config, order: -1 }); // Base layer always at bottom
    this.color = color;
  }

  setColor(color: number): void {
    this.color = color;
    this._notifyChange();
  }

  getRGBAData(vertexCount: number): Float32Array {
    const rgbaData = new Float32Array(vertexCount * 4);
    
    // Convert color to RGB
    const r = ((this.color >> 16) & 255) / 255;
    const g = ((this.color >> 8) & 255) / 255;
    const b = (this.color & 255) / 255;
    
    // Fill all vertices with the base color
    for (let i = 0; i < rgbaData.length; i += 4) {
      rgbaData[i] = r;
      rgbaData[i + 1] = g;
      rgbaData[i + 2] = b;
      rgbaData[i + 3] = this.opacity;
    }
    
    return rgbaData;
  }

  update(updates: BaseLayerUpdateData): void {
    if (updates.color !== undefined) {
      this.setColor(updates.color);
    }
    if (updates.opacity !== undefined) {
      this.setOpacity(updates.opacity);
    }
    if (updates.visible !== undefined) {
      this.setVisible(updates.visible);
    }
  }

  toStateJSON(): Record<string, unknown> {
    return { ...super.toStateJSON(), type: 'base', color: this.color };
  }
}

/**
 * Categorical label layer: maps per-vertex integer labels to solid colors.
 * Useful for parcellations when used inside a MultiLayerNeuroSurface.
 */
export class LabelLayer extends Layer {
  private labels: Uint32Array;
  private labelMap: Map<number, THREE.Color>;
  private defaultColor: THREE.Color;
  private rgbaBuffer: Float32Array | null = null;

  constructor(id: string, options: LabelLayerOptions) {
    super(id, options);

    if (!options.labels || !options.labelDefs) {
      throw new Error('LabelLayer requires labels and labelDefs');
    }

    this.labels = options.labels instanceof Uint32Array
      ? options.labels
      : options.labels instanceof Int32Array
        ? new Uint32Array(options.labels)
        : new Uint32Array(options.labels);

    this.labelMap = new Map();
    options.labelDefs.forEach(def => {
      this.labelMap.set(def.id, new THREE.Color(def.color as any));
    });

    this.defaultColor = new THREE.Color(options.defaultColor ?? 0x999999);
    this.needsUpdate = true;
  }

  setLabels(labels: Uint32Array | Int32Array | number[]): void {
    this.labels = labels instanceof Uint32Array
      ? labels
      : labels instanceof Int32Array
        ? new Uint32Array(labels)
        : new Uint32Array(labels);
    this._notifyChange();
  }

  setLabelDefs(labelDefs: Array<{ id: number; color: THREE.ColorRepresentation }>): void {
    this.labelMap.clear();
    labelDefs.forEach(def => {
      this.labelMap.set(def.id, new THREE.Color(def.color as any));
    });
    this._notifyChange();
  }

  update(data: LabelLayerOptions & LayerUpdateData): void {
    if (data.labels !== undefined) {
      this.setLabels(data.labels);
    }
    if (data.labelDefs !== undefined) {
      this.setLabelDefs(data.labelDefs);
    }
    if (data.defaultColor !== undefined) {
      this.defaultColor = new THREE.Color(data.defaultColor as any);
      this._notifyChange();
    }
    if (data.opacity !== undefined) {
      this.setOpacity(data.opacity);
    }
    if (data.visible !== undefined) {
      this.setVisible(data.visible);
    }
    if (data.blendMode !== undefined) {
      this.setBlendMode(data.blendMode);
    }
  }

  getRGBAData(vertexCount: number): Float32Array {
    if (!this.rgbaBuffer || this.rgbaBuffer.length !== vertexCount * 4) {
      this.rgbaBuffer = new Float32Array(vertexCount * 4);
    }

    const buffer = this.rgbaBuffer;
    const labels = this.labels;
    const map = this.labelMap;
    const defaultColor = this.defaultColor;

    for (let i = 0; i < vertexCount; i++) {
      const labelId = labels[i] ?? -1;
      const color = map.get(labelId) || defaultColor;
      const offset = i * 4;
      buffer[offset] = color.r;
      buffer[offset + 1] = color.g;
      buffer[offset + 2] = color.b;
      buffer[offset + 3] = 1; // alpha; compositing will apply layer opacity
    }

    this.needsUpdate = false;
    return buffer;
  }

  toStateJSON(): Record<string, unknown> {
    return { ...super.toStateJSON(), type: 'label' };
  }
}

/**
 * Layer stack manager
 */
export class LayerStack {
  private layers: Map<string, Layer>;
  private layerOrder: string[];
  needsComposite: boolean;

  constructor() {
    this.layers = new Map();
    this.layerOrder = [];
    this.needsComposite = true;
  }

  addLayer(layer: Layer): void {
    this.layers.set(layer.id, layer);
    this.updateLayerOrder();
    this.needsComposite = true;
    debugLog(`Added layer ${layer.id} to stack`);
  }

  removeLayer(id: string): boolean {
    const layer = this.layers.get(id);
    if (layer) {
      if (layer.dispose) {
        layer.dispose();
      }
      this.layers.delete(id);
      this.updateLayerOrder();
      this.needsComposite = true;
      debugLog(`Removed layer ${id} from stack`);
      return true;
    }
    return false;
  }

  getLayer(id: string): Layer | undefined {
    return this.layers.get(id);
  }

  updateLayer(id: string, updates: LayerUpdateData): void {
    const layer = this.layers.get(id);
    if (layer) {
      layer.update(updates);
      if (layer.needsUpdate) {
        this.needsComposite = true;
      }
    }
  }

  getAllLayers(): Layer[] {
    return Array.from(this.layers.values());
  }

  setLayerOrder(ids: string[]): void {
    // Validate that all ids exist
    const validIds = ids.filter(id => this.layers.has(id));
    
    // Add any missing layers to the end
    this.layers.forEach((layer, id) => {
      if (!validIds.includes(id)) {
        validIds.push(id);
      }
    });
    
    this.layerOrder = validIds;
    this.needsComposite = true;
  }

  updateLayerOrder(): void {
    // Sort layers by order property
    this.layerOrder = Array.from(this.layers.keys()).sort((a, b) => {
      const layerA = this.layers.get(a)!;
      const layerB = this.layers.get(b)!;
      return layerA.order - layerB.order;
    });
  }

  getVisibleLayers(): Layer[] {
    return this.layerOrder
      .map(id => this.layers.get(id)!)
      .filter(layer => layer && layer.visible);
  }

  clear(): void {
    this.layers.forEach(layer => {
      if (layer.dispose) {
        layer.dispose();
      }
    });
    this.layers.clear();
    this.layerOrder = [];
    this.needsComposite = true;
  }

  dispose(): void {
    this.clear();
  }
}
